import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  createProduct,
  updateProduct,
  fetchSingleProduct,
  clearProductState,
} from "../redux/slices/productSlice";
import { fetchProductCategories } from "../redux/slices/productCategorySlice";
import { useNavigate, useParams } from "react-router-dom";

const AddEditProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams(); // edit ke liye

  const { selectedProduct, loading, success, error } = useSelector(
    (state) => state.product
  );
  const { categories } = useSelector(
    (state) => state.productCategory
  );
  const { user } = useSelector((state) => state.auth);
  const vendorId = user?.vendor?._id;

  const [form, setForm] = useState({
    name: "",
    category: "",
    subCategory: "",
    brand: "",
    price: "",
    stock: "",
    description: "",
  });

  const [images, setImages] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  /* ======================
     LOAD DATA
  ====================== */
  useEffect(() => {
    dispatch(fetchProductCategories());
    if (id) dispatch(fetchSingleProduct(id));
  }, [id, dispatch]);

  /* ======================
     SET EDIT DATA
  ====================== */
  useEffect(() => {
    if (selectedProduct && id) {
      setForm({
        name: selectedProduct.name,
        category: selectedProduct.category?._id,
        subCategory: selectedProduct.subCategory?._id || "",
        brand: selectedProduct.brand || "",
        price: selectedProduct.price,
        stock: selectedProduct.stock,
        description: selectedProduct.description || "",
      });
    }
  }, [selectedProduct, id]);

  /* ======================
     FILTER SUB CATEGORY
  ====================== */
  useEffect(() => {
    if (form.category) {
      const subs = categories.filter(
        (c) => c.parent?._id === form.category
      );
      setSubCategories(subs);
    } else {
      setSubCategories([]);
    }
  }, [form.category, categories]);

  /* ======================
     ALERTS
  ====================== */
  useEffect(() => {
    if (success) {
      Swal.fire("Success", "Product saved successfully", "success");
      dispatch(clearProductState());
      navigate("/manage-product");
    }

    if (error) {
      Swal.fire("Error", error, "error");
      dispatch(clearProductState());
    }
  }, [success, error, dispatch, navigate]);

  /* ======================
     HANDLERS
  ====================== */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setImages([...e.target.files]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();

    // Add vendorId automatically
    if (vendorId) formData.append("vendor", vendorId);

    Object.keys(form).forEach((key) =>
      formData.append(key, form[key])
    );

    images.forEach((img) =>
      formData.append("images", img)
    );

    if (id) {
      dispatch(updateProduct({ id, formData }));
    } else {
      dispatch(createProduct(formData));
    }
  };

  /* ======================
     RENDER
  ====================== */
  return (
    <div className="container mt-4">
      <div className="card shadow">
        <div className="card-header bg-dark text-white">
          <h5 className="mb-0 text-white">
            {id ? "Edit Product" : "Add Product"}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">

              <div className="col-md-6">
                <label className="form-label">Product Name</label>
                <input
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* CATEGORY */}
              <div className="col-md-6">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  {categories
                    .filter((c) => !c.parent)
                    .map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* SUB CATEGORY */}
              <div className="col-md-6">
                <label className="form-label">Sub Category</label>
                <select
                  className="form-select"
                  name="subCategory"
                  value={form.subCategory}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  {subCategories.map((sub) => (
                    <option key={sub._id} value={sub._id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Brand</label>
                <input
                  className="form-control"
                  name="brand"
                  value={form.brand}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Price</label>
                <input
                  type="number"
                  className="form-control"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Stock</label>
                <input
                  type="number"
                  className="form-control"
                  name="stock"
                  value={form.stock}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Images</label>
                <input
                  type="file"
                  multiple
                  className="form-control"
                  onChange={handleImageChange}
                />
              </div>

              <div className="col-12 text-end">
                <button
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Product"}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEditProduct;
